/**
 * The shared board: a travertine slab with six carved areas and the time track
 * along its top edge. `staticBoard` draws what never changes in a game — the
 * stone, the map, the roads, the senate steps, the empty spaces — and
 * `boardPieces` draws everything the view decides, on top of it.
 */
import { MANIFESTS, type ActionKind, type PlayerCount, type View } from '@trajan/engine';
import { Container, FillGradient, FillPattern, Graphics } from 'pixi.js';
import type { TargetId } from '../ids';
import {
  AREA_TITLE_H,
  BOARD,
  DECK,
  DISTRICT,
  FORUM,
  MAP,
  MAP_INSET,
  MEDALLION_R,
  PILE_H,
  PILE_W,
  SEAPORT,
  SENATE,
  SHIP_H,
  SHIP_W,
  STACKS,
  STACK_TILE,
  TIME,
  TIME_LABEL,
  YELLOW_CAPTION,
  bonusRect,
  demandSlot,
  forumSpace,
  lapStep,
  pileRect,
  provinceCentre,
  provincePiece,
  provinceTile,
  senateStep,
  shipRect,
  siteRect,
  siteRows,
  stackRect,
  timeStone,
  yellowSpace,
  type Point,
  type Rect,
} from '../layout';
import { parchmentTexture, stoneTexture, waterTexture } from '../materials';
import {
  ACCENT,
  BRONZE,
  BRONZE_DARK,
  BRONZE_LIGHT,
  ENGRAVE_LIGHT,
  INK,
  INK_SOFT,
  PARCHMENT,
  STONE,
  TILE,
  WATER,
  mix,
} from '../palette';
import { stackTitle } from '../../panel/describe';
import { icon } from './icons';
import {
  boxShadow,
  cardBack,
  emptySpace,
  label,
  leaderPawn,
  seatToken,
  senateDisc,
} from './pieces';
import {
  bonusFace,
  buildingFace,
  commodityCard,
  extraTileFace,
  forumTileFace,
  trajanFace,
} from './tiles';

/**
 * Records what the canvas drew: the object that stands for each target, and
 * every piece with an identity that lasts from one view to the next — a tile, a
 * card, a leader — so the scene can glide it from where it was to where it is.
 */
export interface Register {
  (id: TargetId, obj: Container): void;
  piece: (key: string, obj: Container) => void;
}

const at = <T extends Container>(parent: Container, child: T, x: number, y: number): T => {
  child.position.set(x, y);
  parent.addChild(child);
  return child;
};

// ---------------------------------------------------------------------------
// The slab and its carved areas
// ---------------------------------------------------------------------------

function slab(parent: Container) {
  const r = BOARD;
  at(parent, boxShadow(r.w, r.h, 12, 0.7, 16), r.x, r.y);
  const g = new Graphics();
  g.roundRect(r.x, r.y + 5, r.w, r.h, 18).fill(STONE.dark);
  g.roundRect(r.x, r.y, r.w, r.h, 18).fill(new FillPattern(stoneTexture(), 'repeat'));
  g.roundRect(r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, 17).stroke({
    width: 2,
    color: 0xffffff,
    alpha: 0.4,
  });
  g.roundRect(r.x, r.y, r.w, r.h, 18).stroke({ width: 1, color: BRONZE_DARK, alpha: 0.45 });
  // A bronze rule inset from the edge, the way a good board is framed.
  g.roundRect(r.x + 6, r.y + 6, r.w - 12, r.h - 12, 13).stroke({
    width: 1.2,
    color: BRONZE,
    alpha: 0.55,
  });
  parent.addChild(g);
}

/** A carved recess with its engraved title. */
function area(parent: Container, r: Rect, title: string, glyph: ActionKind | null) {
  const g = new Graphics();
  g.roundRect(r.x, r.y, r.w, r.h, 12).fill({ color: 0x5c4424, alpha: 0.1 });
  g.roundRect(r.x + 1, r.y + 1.5, r.w - 2, r.h - 2.5, 11).stroke({
    width: 2.5,
    color: 0x3b2a14,
    alpha: 0.1,
  });
  g.roundRect(r.x, r.y, r.w, r.h, 12).stroke({ width: 1, color: 0x6a4e2a, alpha: 0.38 });
  g.moveTo(r.x + 14, r.y + r.h + 1)
    .lineTo(r.x + r.w - 14, r.y + r.h + 1)
    .stroke({ width: 1.2, color: 0xffffff, alpha: 0.55 });
  parent.addChild(g);

  let x = r.x + 14;
  if (glyph) {
    at(parent, icon(glyph, 16, BRONZE_DARK), x + 8, r.y + 16);
    x += 22;
  }
  const under = at(
    parent,
    label(title.toUpperCase(), 14, {
      face: 'display',
      fill: ENGRAVE_LIGHT,
      anchor: [0, 0.5],
      spacing: 1.6,
    }),
    x,
    r.y + 17,
  );
  under.alpha = 0.75;
  at(
    parent,
    label(title.toUpperCase(), 14, {
      face: 'display',
      fill: BRONZE_DARK,
      anchor: [0, 0.5],
      spacing: 1.6,
    }),
    x,
    r.y + 16,
  );
}

/** A small note at the right end of an area's title band. */
function note(parent: Container, r: Rect, text: string) {
  at(
    parent,
    label(text, 12.5, { fill: INK_SOFT, anchor: [1, 0.5], weight: '500' }),
    r.x + r.w - 14,
    r.y + 16,
  );
}

// ---------------------------------------------------------------------------
// The map
// ---------------------------------------------------------------------------

/** The coast of the mainland, from the top edge round to the bottom, in parchment coordinates. */
const COAST: [number, number][] = [
  [344, 0],
  [322, 14],
  [304, 30],
  [288, 46],
  [246, 54],
  [206, 60],
  [174, 72],
  [140, 86],
  [108, 94],
  [80, 102],
  [50, 106],
  [34, 120],
  [38, 140],
  [26, 162],
  [12, 182],
  [8, 212],
  [12, 240],
  [18, 256],
];
const BRITANNIA: [number, number][] = [
  [42, 18],
  [72, 6],
  [108, 10],
  [130, 28],
  [126, 56],
  [102, 78],
  [64, 82],
  [36, 66],
  [28, 40],
];

const toMap = ([x, y]: [number, number]): Point => ({ x: MAP_INSET.x + x, y: MAP_INSET.y + y });

function polygon(points: [number, number][]): number[] {
  return points.flatMap((p) => {
    const q = toMap(p);
    return [q.x, q.y];
  });
}

function dashes(g: Graphics, a: Point, b: Point, dash: number, gap: number) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  for (let d = 0; d < len; d += dash + gap) {
    const e = Math.min(len, d + dash);
    g.moveTo(a.x + ux * d, a.y + uy * d).lineTo(a.x + ux * e, a.y + uy * e);
  }
}

/** Shorten a line at both ends by `by`, so a road meets a medallion's rim, not its middle. */
function trim(a: Point, b: Point, by: number): [Point, Point] {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const ux = (b.x - a.x) / len;
  const uy = (b.y - a.y) / len;
  return [
    { x: a.x + ux * by, y: a.y + uy * by },
    { x: b.x - ux * by, y: b.y - uy * by },
  ];
}

function mapInset(parent: Container) {
  const r = MAP_INSET;
  const land = new Graphics();
  land.roundRect(r.x, r.y, r.w, r.h, 8).fill(new FillPattern(parchmentTexture(), 'repeat'));
  parent.addChild(land);

  // The sea to the west and north, and Britannia out in it.
  const sea = new Graphics();
  sea
    .poly([r.x, r.y, ...polygon(COAST), r.x, r.y + r.h])
    .fill(new FillPattern(waterTexture(), 'repeat'));
  sea.alpha = 0.55;
  parent.addChild(sea);
  const island = new Graphics()
    .poly(polygon(BRITANNIA))
    .fill(new FillPattern(parchmentTexture(), 'repeat'));
  parent.addChild(island);
  const coast = new Graphics();
  coast
    .poly(polygon(COAST), false)
    .stroke({ width: 1.6, color: mix(WATER.dark, PARCHMENT.dark, 0.4), alpha: 0.7 });
  coast
    .poly(polygon(BRITANNIA))
    .stroke({ width: 1.6, color: mix(WATER.dark, PARCHMENT.dark, 0.4), alpha: 0.7 });
  parent.addChild(coast);

  // Aged edges.
  const vignette = new Graphics();
  vignette.roundRect(r.x, r.y, r.w, r.h, 8).fill(
    new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.5 },
      innerRadius: 0.32,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.72,
      colorStops: [
        { offset: 0, color: 'rgba(120, 84, 40, 0)' },
        { offset: 1, color: 'rgba(120, 84, 40, 0.42)' },
      ],
      textureSpace: 'local',
    }),
  );
  parent.addChild(vignette);
  const frame = new Graphics()
    .roundRect(r.x, r.y, r.w, r.h, 8)
    .stroke({ width: 1.5, color: BRONZE_DARK, alpha: 0.5 });
  parent.addChild(frame);

  // Roads are the rules: a leader moves along one, and only along one.
  const roads = new Graphics();
  const lanes = new Graphics();
  const campRoads = new Graphics();
  for (const p of MANIFESTS.provinces) {
    for (const n of p.adjacent) {
      if (p.id > n) continue;
      const [a, b] = trim(provinceCentre(p.id), provinceCentre(n), MEDALLION_R + 3);
      if (p.id === 'britannia' || n === 'britannia') {
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        for (let d = 0; d <= len; d += 7) {
          lanes.circle(a.x + ((b.x - a.x) * d) / len, a.y + ((b.y - a.y) * d) / len, 1.3);
        }
      } else {
        roads.moveTo(a.x, a.y).lineTo(b.x, b.y);
      }
    }
  }
  roads.stroke({ width: 5, color: PARCHMENT.light, alpha: 0.9, cap: 'round' });
  const ruts = new Graphics();
  for (const p of MANIFESTS.provinces) {
    for (const n of p.adjacent) {
      if (p.id > n || p.id === 'britannia' || n === 'britannia') continue;
      const [a, b] = trim(provinceCentre(p.id), provinceCentre(n), MEDALLION_R + 3);
      dashes(ruts, a, b, 5, 3.5);
    }
  }
  ruts.stroke({ width: 1.6, color: mix(INK_SOFT, PARCHMENT.dark, 0.2), alpha: 0.9 });
  lanes.fill({ color: WATER.dark, alpha: 0.85 });
  for (const id of MANIFESTS.campAdjacent) {
    const [a, b] = trim(provinceCentre('italia'), provinceCentre(id), MEDALLION_R + 3);
    dashes(campRoads, a, b, 6, 4);
  }
  campRoads.stroke({ width: 2, color: ACCENT, alpha: 0.75 });
  parent.addChild(roads, ruts, lanes, campRoads);

  for (const p of MANIFESTS.provinces) medallion(parent, p.id, p.name, String(p.vp));
  medallion(parent, 'italia', MANIFESTS.camp, null);
}

function medallion(parent: Container, id: string, name: string, vp: string | null) {
  const c = provinceCentre(id);
  const g = new Graphics();
  g.circle(c.x + 1, c.y + 2.5, MEDALLION_R + 3).fill({ color: 0x000000, alpha: 0.25 });
  g.circle(c.x, c.y, MEDALLION_R + 3).fill(
    new FillGradient({
      type: 'linear',
      start: { x: 0.2, y: 0 },
      end: { x: 0.8, y: 1 },
      colorStops: [
        { offset: 0, color: BRONZE_LIGHT },
        { offset: 0.5, color: BRONZE },
        { offset: 1, color: BRONZE_DARK },
      ],
      textureSpace: 'local',
    }),
  );
  g.circle(c.x, c.y, MEDALLION_R - 0.5).fill(vp === null ? 0xf2e4c4 : 0xf6ecd4);
  g.circle(c.x, c.y, MEDALLION_R - 0.5).stroke({ width: 1, color: BRONZE_DARK, alpha: 0.6 });
  parent.addChild(g);
  if (vp === null) at(parent, icon('camp', 24, ACCENT), c.x, c.y);
  else at(parent, label(vp, 17, { face: 'display', fill: INK }), c.x, c.y + 0.5);
  at(
    parent,
    label(name, 13, { fill: INK, weight: '700', halo: PARCHMENT.light }),
    c.x,
    c.y + MEDALLION_R + 12,
  );
}

// ---------------------------------------------------------------------------
// The whole static board
// ---------------------------------------------------------------------------

/** Everything that stays put for a whole game with this many players and this lap. */
export function staticBoard(playerCount: PlayerCount, lap: number): Container {
  const root = new Container();
  slab(root);

  // time track: the lap's stones, the start one crowned with laurel
  const track = new Graphics();
  const first = timeStone(0, lap);
  const last = timeStone(lap - 1, lap);
  track
    .roundRect(
      first.x - first.r - 6,
      first.y - first.r - 5,
      last.x - first.x + 2 * first.r + 12,
      2 * first.r + 10,
      first.r + 5,
    )
    .fill({ color: 0x5c4424, alpha: 0.1 })
    .stroke({ width: 1, color: 0x6a4e2a, alpha: 0.3 });
  for (let k = 0; k < lap; k++) {
    const s = timeStone(k, lap);
    track.circle(s.x, s.y + 1.5, s.r).fill({ color: 0x000000, alpha: 0.18 });
    track
      .circle(s.x, s.y, s.r)
      .fill(k === 0 ? 0xe8d49a : STONE.light)
      .stroke({ width: 1, color: STONE.dark });
  }
  root.addChild(track);
  at(root, icon('laurel', first.r * 1.8, BRONZE), first.x, first.y);
  at(
    root,
    label('THE PEOPLE\nDEMAND', 11, {
      face: 'display',
      fill: BRONZE_DARK,
      anchor: [1, 0.5],
      spacing: 1,
      align: 'right',
    }),
    demandSlot(0).x - 12,
    TIME.y + TIME.h / 2,
  );
  for (let i = 0; i < 3; i++) {
    const d = demandSlot(i);
    at(root, emptySpace(d.w, d.h, 6), d.x, d.y);
  }

  area(root, FORUM, 'Forum', 'forum');
  area(root, MAP, 'Provinces', 'military');
  area(root, DISTRICT, 'Construction', 'construction');
  area(root, SEAPORT, 'Seaport', 'seaport');
  area(root, STACKS, 'Trajan tiles', 'trajan');
  area(root, SENATE, 'Senate', 'senate');

  // forum spaces and the three yellow ones
  const spaces =
    MANIFESTS.setup.forumSpacesByPlayerCount[
      String(playerCount) as keyof typeof MANIFESTS.setup.forumSpacesByPlayerCount
    ];
  for (let i = 0; i < spaces; i++) {
    const s = forumSpace(i);
    at(root, emptySpace(s.w, s.h), s.x, s.y);
  }
  for (let i = 0; i < MANIFESTS.setup.yellowSpaces; i++) {
    const s = yellowSpace(i);
    const g = at(root, emptySpace(s.w, s.h), s.x, s.y);
    g.tint = 0xf3d98a;
  }
  const cap = YELLOW_CAPTION;
  at(
    root,
    label('extra\nactions', 10.5, { face: 'display', fill: BRONZE_DARK, spacing: 0.6 }),
    cap.x + cap.w / 2,
    cap.y + cap.h / 2,
  );

  mapInset(root);

  // the district's lots
  const rows = siteRows(playerCount);
  for (let i = 0; i < rows * MANIFESTS.constructionGrid.columns; i++) {
    const s = siteRect(i, rows);
    const lot = new Graphics();
    lot.roundRect(s.x, s.y, s.w, s.h, 5).fill({ color: 0x8c7b62, alpha: 0.28 });
    lot
      .roundRect(s.x + 0.5, s.y + 0.5, s.w - 1, s.h - 1, 5)
      .stroke({ width: 1, color: 0x4a3a24, alpha: 0.3 });
    // paving joints, so a built lot still reads as ground
    lot.moveTo(s.x + 4, s.y + s.h / 2).lineTo(s.x + s.w - 4, s.y + s.h / 2);
    lot.moveTo(s.x + s.w / 3, s.y + 4).lineTo(s.x + s.w / 3, s.y + s.h / 2);
    lot.moveTo(s.x + (2 * s.w) / 3, s.y + s.h / 2).lineTo(s.x + (2 * s.w) / 3, s.y + s.h - 4);
    lot.stroke({ width: 1, color: 0x4a3a24, alpha: 0.16 });
    root.addChild(lot);
  }

  // harbour water, and the places the deck and the discards lie
  const harbour = new Graphics();
  harbour
    .roundRect(SEAPORT.x + 6, SEAPORT.y + AREA_TITLE_H + 2, SEAPORT.w - 12, SHIP_H + 6, 8)
    .fill(new FillPattern(waterTexture(), 'repeat'));
  harbour
    .roundRect(SEAPORT.x + 6, SEAPORT.y + AREA_TITLE_H + 2, SEAPORT.w - 12, SHIP_H + 6, 8)
    .stroke({
      width: 1.4,
      color: BRONZE_DARK,
      alpha: 0.5,
    });
  root.addChild(harbour);
  at(root, emptySpace(PILE_W, PILE_H, 5), DECK.x, DECK.y);
  for (let i = 0; i < 2; i++) {
    const p = pileRect(i);
    at(root, emptySpace(p.w, p.h, 5), p.x, p.y);
  }

  // Trajan stacks' places
  for (let i = 0; i < 6; i++) {
    const s = stackRect(i);
    at(root, emptySpace(s.w, s.h), s.x, s.y);
  }

  // senate steps, rising from the start space
  MANIFESTS.senateTrack.spaces.forEach((sp, i) => {
    const s = senateStep(i);
    const g = new Graphics();
    g.roundRect(s.x, s.y + 3, s.w, s.h, 4).fill(STONE.dark);
    g.roundRect(s.x, s.y, s.w, s.h, 4).fill(
      new FillGradient({
        type: 'linear',
        start: { x: 0, y: 0 },
        end: { x: 1, y: 0.4 },
        colorStops: [
          { offset: 0, color: 0xfffaf0 },
          { offset: 1, color: STONE.mid },
        ],
        textureSpace: 'local',
      }),
    );
    g.roundRect(s.x, s.y, s.w, s.h, 4).stroke({ width: 1, color: STONE.dark });
    root.addChild(g);
    at(
      root,
      label(String(sp.vp), 17, { face: 'display', fill: INK }),
      s.x + s.w / 2,
      s.y + s.h - 26,
    );
    at(
      root,
      label(`${sp.votes} votes`, 9.5, { fill: INK_SOFT, weight: '700' }),
      s.x + s.w / 2,
      s.y + s.h - 10,
    );
  });
  for (let i = 0; i < 2; i++) {
    const b = bonusRect(i);
    at(root, emptySpace(b.w, b.h), b.x, b.y);
  }
  return root;
}

// ---------------------------------------------------------------------------
// Everything the view decides
// ---------------------------------------------------------------------------

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

/** At setup, the stacks whose top tile is in hand, or already put on a slot of the mat. */
export type StackMarks = ReadonlyMap<number, 'held' | number>;

export function boardPieces(view: View, register: Register, marks?: StackMarks): Container {
  const root = new Container();
  timeTrack(root, view, register);
  forum(root, view, register);
  provinces(root, view, register);
  district(root, view, register);
  seaport(root, view, register);
  stacks(root, view, register, marks);
  senate(root, view, register);
  return root;
}

function timeTrack(root: Container, view: View, register: Register) {
  const { length, round, quarter } = view.time;
  const step = lapStep(view.time);
  const passed = new Graphics();
  for (let k = 1; k <= step; k++) {
    const s = timeStone(k, length);
    passed.circle(s.x, s.y, s.r - 2).fill({ color: BRONZE, alpha: 0.28 });
  }
  root.addChild(passed);
  const s = timeStone(step, length);
  const marker = new Container();
  const g = new Graphics();
  g.circle(0, 2, s.r + 1).fill({ color: 0x000000, alpha: 0.35 });
  g.circle(0, 0, s.r + 1).fill(
    new FillGradient({
      type: 'radial',
      center: { x: 0.35, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.6,
      colorStops: [
        { offset: 0, color: BRONZE_LIGHT },
        { offset: 1, color: BRONZE_DARK },
      ],
      textureSpace: 'local',
    }),
  );
  marker.addChild(g);
  at(marker, icon('hourglass', s.r * 1.3, 0xfff1d0), 0, 0);
  at(root, marker, s.x, s.y);
  register('time', marker);
  register.piece('time', marker);

  const lbl = TIME_LABEL;
  at(
    root,
    label(`QUARTER ${ROMAN[quarter - 1] ?? quarter}`, 16, {
      face: 'display',
      fill: BRONZE_DARK,
      anchor: [0, 0.5],
      spacing: 1.4,
    }),
    lbl.x + 8,
    lbl.y + 14,
  );
  at(
    root,
    label(
      `round ${round} of ${MANIFESTS.timeTrack.roundsPerQuarter} · ${length - step} to the round's end`,
      12,
      {
        fill: INK_SOFT,
        anchor: [0, 0.5],
      },
    ),
    lbl.x + 8,
    lbl.y + 34,
  );

  view.demands.forEach((d, i) => {
    const r = demandSlot(i);
    const c = at(root, new Container(), r.x, r.y);
    const face = forumTileFace({ id: d.id, kind: 'demand', icon: d.icon }, r.w);
    c.addChild(face);
    register(`demand:${i}`, c);
  });
}

function forum(root: Container, view: View, register: Register) {
  view.board.forum.forEach((t, i) => {
    if (!t) return;
    const s = forumSpace(i);
    const c = at(root, forumTileFace(t, s.w), s.x, s.y);
    register(`forum:${i}`, c);
    register.piece(t.id, c);
  });
  view.board.yellowSpaces.forEach((t, i) => {
    if (!t) return;
    const s = yellowSpace(i);
    const c = at(root, extraTileFace(t, s.w), s.x, s.y);
    register(`yellow:${i}`, c);
    register.piece(t.id, c);
  });
  note(root, FORUM, `${view.board.forum.filter(Boolean).length} tiles`);
}

function provinces(root: Container, view: View, register: Register) {
  for (const p of MANIFESTS.provinces) {
    const live = view.board.provinces[p.id];
    const node = new Container();
    root.addChild(node);
    register(`province:${p.id}`, node);
    if (live?.tile) {
      const r = provinceTile(p.id);
      register.piece(live.tile.id, at(node, forumTileFace(live.tile, r.w), r.x, r.y));
    }
    const legion = live?.legionnaires ?? [];
    legion.forEach((seat, k) => {
      const q = provincePiece(p.id, 'left', k, legion.length);
      at(node, seatToken(seat, 5.5), q.x, q.y);
    });
    const leaders = view.players.filter((pl) => pl.leader === p.id);
    leaders.forEach((pl, k) => {
      const q = provincePiece(p.id, 'right', k, leaders.length);
      register.piece(`leader-${pl.id}`, at(node, leaderPawn(pl.id, 15), q.x, q.y + 2));
    });
  }
  const camp = new Container();
  root.addChild(camp);
  register('italia', camp);
  const atCamp = view.players.filter((pl) => pl.leader === 'camp');
  atCamp.forEach((pl, k) => {
    const q = provincePiece('italia', 'right', k, atCamp.length);
    register.piece(`leader-${pl.id}`, at(camp, leaderPawn(pl.id, 15), q.x, q.y + 2));
  });
}

function district(root: Container, view: View, register: Register) {
  const rows = siteRows(view.playerCount);
  view.board.sites.forEach((site, i) => {
    const s = siteRect(i, rows);
    const c = at(root, new Container(), s.x, s.y);
    register(`site:${i}`, c);
    if (site.tile)
      register.piece(site.tile.id, at(c, buildingFace(site.tile, s.w - 6, s.h - 6), 3, 3));
    site.workers.forEach((seat, k) => at(c, seatToken(seat, 5.5), s.w - 10 - k * 13, s.h - 9));
  });
  note(root, DISTRICT, `${view.board.sites.filter((s) => s.tile).length} tiles left`);
}

const SET_EMBLEM: Record<string, (g: Graphics, x: number, y: number, ink: number) => void> = {
  identical: (g, x, y, ink) => {
    for (let k = -1; k <= 1; k++) g.rect(x + k * 8 - 3, y - 3, 6, 6);
    g.fill(ink);
  },
  different: (g, x, y, ink) => {
    g.circle(x - 8, y, 3.2).fill(ink);
    g.poly([x, y - 3.6, x + 3.6, y + 3, x - 3.6, y + 3]).fill(ink);
    g.rect(x + 5, y - 3, 6, 6).fill(ink);
  },
  pairs: (g, x, y, ink) => {
    g.rect(x - 11, y - 3, 5, 5)
      .rect(x - 5, y - 3, 5, 5)
      .fill(ink);
    g.circle(x + 5, y - 0.5, 2.6)
      .circle(x + 10.5, y - 0.5, 2.6)
      .fill(ink);
  },
};

function seaport(root: Container, view: View, register: Register) {
  view.board.ships.forEach((ship, i) => {
    const r = shipRect(i);
    const table = MANIFESTS.ships.find((s) => s.id === ship.id);
    const grey = ship.side === 'grey';
    const c = at(root, new Container(), r.x, r.y);
    register(`ship:${ship.id}`, c);
    const g = new Graphics();
    // the galley: a square sail on a mast, over a hull and its oars
    const hull = grey ? 0x5a5550 : 0x5b3a1f;
    const sail = grey ? 0xbdb8ad : 0xf4ead2;
    g.rect(SHIP_W / 2 - 1.3, 3, 2.6, 36).fill(0x3a2716);
    g.roundRect(SHIP_W / 2 - 25, 6, 50, 24, 3)
      .fill(sail)
      .stroke({ width: 1, color: 0x6b5a45 });
    g.moveTo(SHIP_W / 2 - 25, 6)
      .lineTo(SHIP_W / 2 + 25, 6)
      .stroke({ width: 2, color: 0x3a2716 });
    for (let k = 0; k < 8; k++) g.moveTo(10 + k * 10, 41).lineTo(6 + k * 10, 50);
    g.stroke({ width: 1.3, color: 0x2e1f12, alpha: 0.8 });
    g.poly([4, 33, SHIP_W - 4, 33, SHIP_W - 14, 44, 14, 44])
      .fill(hull)
      .stroke({ width: 1, color: 0x2e1f12 });
    g.poly([SHIP_W - 4, 33, SHIP_W + 2, 28, SHIP_W - 2, 36]).fill(hull);
    SET_EMBLEM[ship.set]?.(g, SHIP_W / 2, 18, grey ? 0x77706a : ACCENT);
    c.addChild(g);
    // the plaque under the keel: the set it takes, and what it pays
    const side = table ? (grey ? table.grey : table.colour) : {};
    const plaque = new Graphics()
      .roundRect(3, 52, SHIP_W - 6, SHIP_H - 53, 4)
      .fill({ color: grey ? 0xd9d4ca : 0xf4ead2, alpha: 0.97 })
      .stroke({ width: 1, color: BRONZE_DARK, alpha: 0.6 });
    c.addChild(plaque);
    at(
      c,
      label(ship.set.toUpperCase(), 9, {
        face: 'display',
        fill: grey ? INK_SOFT : ACCENT,
        spacing: 0.6,
      }),
      SHIP_W / 2,
      60,
    );
    Object.entries(side).forEach(([n, vp], k) => {
      const col = k % 2;
      const row = Math.floor(k / 2);
      at(
        c,
        label(`${n}: ${vp}`, 11.5, { fill: grey ? INK_SOFT : INK, weight: '700' }),
        24 + col * 40,
        73 + row * 12,
      );
    });
  });

  // the deck, and the two discard piles showing their top card
  const deck = at(root, new Container(), DECK.x, DECK.y);
  register('deck', deck);
  const n = view.hiddenCounts.drawPile;
  // A thicker pile for a fuller deck: the cards underneath show along the bottom edge.
  const layers = Math.min(3, Math.floor(n / 15));
  for (let k = layers; k >= 1; k--)
    at(deck, cardBack(PILE_W, PILE_H, k === layers), k * 1.2, k * 1.6);
  if (n > 0) at(deck, cardBack(PILE_W, PILE_H, layers === 0), 0, 0);
  at(
    deck,
    label(String(n), 13, { face: 'display', fill: 0xfff1d0, halo: 0x3d1510 }),
    PILE_W / 2,
    PILE_H / 2,
  );

  view.discards.forEach((pile, i) => {
    const r = pileRect(i);
    const c = at(root, new Container(), r.x, r.y);
    register(`pile:${i}`, c);
    const top = pile[pile.length - 1];
    if (top) register.piece(top.id, c.addChild(commodityCard(top, PILE_W, PILE_H)));
  });
}

function stacks(root: Container, view: View, register: Register, marks?: StackMarks) {
  view.board.trajanStacks.forEach((stack, i) => {
    const r = stackRect(i);
    const c = at(root, new Container(), r.x, r.y);
    register(`stack:${i}`, c);
    const top = stack[0];
    const kind = MANIFESTS.tiles.trajanStacks[i] ?? '';
    if (top) {
      const depth = Math.min(4, Math.ceil(stack.length / 3));
      const g = new Graphics();
      for (let k = depth; k >= 1; k--) {
        g.roundRect(k * 1.5, k * 2.2, STACK_TILE, STACK_TILE, 7).fill(
          mix(TILE.trajan.dark, 0x000000, 0.1 * k),
        );
      }
      c.addChild(g);
      const mark = marks?.get(i);
      if (mark === 'held') {
        // Lifted off its stack, on its way to the mat.
        at(c, boxShadow(STACK_TILE, STACK_TILE, 12, 0.55, 6), 0, -10);
        register.piece(top.id, at(c, trajanFace(top, STACK_TILE, false), 0, -12));
      } else {
        const face = at(c, trajanFace(top, STACK_TILE, false), 0, 0);
        register.piece(top.id, face);
        if (mark !== undefined) {
          // Already on a slot of the mat: the stack shows where it went.
          face.alpha = 0.35;
          const tag = new Graphics().roundRect(
            STACK_TILE / 2 - 22,
            STACK_TILE / 2 - 12,
            44,
            24,
            12,
          );
          tag.fill(BRONZE_DARK).stroke({ width: 1.5, color: BRONZE_LIGHT });
          c.addChild(tag);
          at(
            c,
            label(`→ ${ROMAN[mark] ?? ''}`, 13, { face: 'display', fill: 0xfff1d6 }),
            STACK_TILE / 2,
            STACK_TILE / 2 + 0.5,
          );
        }
      }
    }
    at(
      root,
      label(stackTitle(kind), 13, { fill: BRONZE_DARK, weight: '700' }),
      r.x + r.w / 2,
      r.y + r.h + 17,
    );
    at(
      root,
      label(top ? `${stack.length} left` : 'empty', 12, { fill: INK_SOFT }),
      r.x + r.w / 2,
      r.y + r.h + 33,
    );
  });
}

function senate(root: Container, view: View, register: Register) {
  MANIFESTS.senateTrack.spaces.forEach((_, i) => {
    const s = senateStep(i);
    const c = at(root, new Container(), s.x, s.y);
    register(`senate:${i}`, c);
    (view.board.senateStacks[i] ?? []).forEach((seat, k) =>
      register.piece(`disc-${seat}`, at(c, senateDisc(seat, 26), s.w / 2, -3 - k * 6)),
    );
  });
  view.board.senateBonus.forEach((t, i) => {
    const r = bonusRect(i);
    const c = at(root, new Container(), r.x, r.y);
    register(`bonus:${i}`, c);
    if (t) register.piece(t.id, c.addChild(bonusFace(t, r.w, r.h, 'both', bonusCaption(t))));
  });
  at(
    root,
    label('CONSUL', 10.5, { face: 'display', fill: BRONZE_DARK, spacing: 1 }),
    bonusRect(0).x + bonusRect(0).w / 2,
    bonusRect(0).y - 9,
  );
  at(
    root,
    label('VICE-CONSUL', 10.5, { face: 'display', fill: BRONZE_DARK, spacing: 1 }),
    bonusRect(1).x + bonusRect(1).w / 2,
    bonusRect(1).y - 9,
  );
}

/** What a bonus tile pays for, in a few words. */
export function bonusCaption(t: {
  kind: string;
  icon?: string | undefined;
  commodities?: string[] | undefined;
}): string {
  switch (t.kind) {
    case 'demand-icon':
      return `holding a ${t.icon === 'helmet' ? 'games' : t.icon === 'flame' ? 'religion' : (t.icon ?? '')} tile`;
    case 'per-display-card':
      return `per ${(t.commodities ?? []).join(' or ') || 'card'} on display`;
    case 'per-worker-on-site':
      return 'per worker on a site';
    case 'per-legionnaire-in-province':
      return 'per legionnaire in a province';
    case 'per-yellow-tile':
      return 'per yellow bonus tile';
    default:
      return t.kind.replace(/-/g, ' ');
  }
}
